import { Trash2, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { NumericInput, SelectInput, TextInput } from '../inputs';
import { LineItemDetail, PartEntry, ReferenceEntry } from '../../types/asn';

const UOM_OPTIONS = [
  { value: 'PC', label: 'Pieces' },
  { value: 'EA', label: 'Each' },
  { value: 'BX', label: 'Box' },
  { value: 'CA', label: 'Case' },
  { value: 'PK', label: 'Package' },
];

const PART_QUALIFIER_OPTIONS = [
  { value: 'BP', label: 'Buyer Part Number' },
  { value: 'VP', label: 'Vendor/Supplier Part Number' },
  { value: 'EC', label: 'Engineering Change Level' },
  { value: 'CH', label: 'Country of Origin' },
];

const REFERENCE_QUALIFIER_OPTIONS = [
  { value: 'PK', label: 'Packing List Number' },
  { value: 'CN', label: 'Carrier Reference Number' },
  { value: 'LS', label: 'Serial/Barcode Number' },
];

// X12 element 103 is a composite: 3-char packaging form + 2-char material.
// CTN25 (carton / corrugated-solid fiber) is the valid pair for carton, the way
// PLT71 is for pallet. The bare 3-char codes below predate that and are kept
// ONLY so documents already holding them still render a selected option —
// a controlled <select> with no matching <option> shows blank.
const PACKAGING_OPTIONS = [
  { value: 'PLT71', label: 'Pallet' },
  { value: 'CTN25', label: 'Carton' },
  { value: 'BOX', label: 'Box (legacy)' },
  { value: 'CTN', label: 'Carton (legacy)' },
  { value: 'DRM', label: 'Drum (legacy)' },
  { value: 'CRT', label: 'Crate (legacy)' },
];

interface LineItemSectionProps {
  lineItem: LineItemDetail;
  lineItemIndex: number;
  orderIndex: number;
  onChange: (orderIndex: number, lineItemIndex: number, field: string, value: any) => void;
  onRemove: (orderIndex: number, lineItemIndex: number) => void;
  errors?: Record<string, string>;
}

export function LineItemSection({
  lineItem,
  lineItemIndex,
  orderIndex,
  onChange,
  onRemove,
  errors = {},
}: LineItemSectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const handleAddPart = () => {
    const newParts = [...lineItem.parts, { qualifier: 'BP', id: '' }];
    onChange(orderIndex, lineItemIndex, 'parts', newParts);
  };

  const handleRemovePart = (partIndex: number) => {
    const newParts = lineItem.parts.filter((_, i) => i !== partIndex);
    onChange(orderIndex, lineItemIndex, 'parts', newParts);
  };

  const handlePartChange = (partIndex: number, field: keyof PartEntry, value: string) => {
    const newParts = [...lineItem.parts];
    newParts[partIndex] = { ...newParts[partIndex], [field]: value };
    onChange(orderIndex, lineItemIndex, 'parts', newParts);
  };

  const handleAddReference = () => {
    const newReferences = [...lineItem.references, { qualifier: 'PK', id: '' }];
    onChange(orderIndex, lineItemIndex, 'references', newReferences);
  };

  const handleRemoveReference = (refIndex: number) => {
    const newReferences = lineItem.references.filter((_, i) => i !== refIndex);
    onChange(orderIndex, lineItemIndex, 'references', newReferences);
  };

  const handleReferenceChange = (refIndex: number, field: keyof ReferenceEntry, value: string) => {
    const newReferences = [...lineItem.references];
    newReferences[refIndex] = { ...newReferences[refIndex], [field]: value };
    onChange(orderIndex, lineItemIndex, 'references', newReferences);
  };

  const handleLoadsChange = (field: string, value: any) => {
    const newLoads = { ...lineItem.loads, [field]: value };
    onChange(orderIndex, lineItemIndex, 'loads', newLoads);
  };

  const handleAddSerial = () => {
    const newSerials = [...lineItem.loads.serials, ''];
    handleLoadsChange('serials', newSerials);
  };

  const handleRemoveSerial = (serialIndex: number) => {
    const newSerials = lineItem.loads.serials.filter((_, i) => i !== serialIndex);
    handleLoadsChange('serials', newSerials);
  };

  const handleSerialChange = (serialIndex: number, value: string) => {
    const newSerials = [...lineItem.loads.serials];
    newSerials[serialIndex] = value;
    handleLoadsChange('serials', newSerials);
  };

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
      <div className="bg-gray-50 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-gray-600 hover:text-gray-700"
          >
            {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          <h6 className="font-medium text-gray-900 text-sm">
            Line {lineItem.line_number} - {lineItem.unitsShipped} {lineItem.uom}
          </h6>
        </div>
        <button
          type="button"
          onClick={() => onRemove(orderIndex, lineItemIndex)}
          className="text-red-600 hover:text-red-700 p-1 rounded hover:bg-red-50"
        >
          <Trash2 size={16} />
        </button>
      </div>

      {isExpanded && (
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <NumericInput
              label="Line Number"
              value={String(lineItem.line_number)}
              onChange={(value) => onChange(orderIndex, lineItemIndex, 'line_number', parseInt(value) || 1)}
              required
              error={errors[`line_${orderIndex}_${lineItemIndex}_line_number`]}
              helperText="Sequence number"
            />
            <NumericInput
              label="Units Shipped"
              value={lineItem.unitsShipped}
              onChange={(value) => onChange(orderIndex, lineItemIndex, 'unitsShipped', value)}
              name={`orders[${orderIndex}].details[${lineItemIndex}].unitsShipped`}
              required
              error={errors[`line_${orderIndex}_${lineItemIndex}_unitsShipped`]}
              helperText="Quantity"
            />
            <SelectInput
              label="Unit of Measure"
              value={lineItem.uom}
              onChange={(value) => onChange(orderIndex, lineItemIndex, 'uom', value)}
              options={UOM_OPTIONS}
              name={`orders[${orderIndex}].details[${lineItemIndex}].uom`}
              required
              error={errors[`line_${orderIndex}_${lineItemIndex}_uom`]}
            />
            <TextInput
              label="Customer Order #"
              value={lineItem.customerOrderNumber}
              onChange={(value) => onChange(orderIndex, lineItemIndex, 'customerOrderNumber', value)}
              name={`orders[${orderIndex}].details[${lineItemIndex}].customerOrderNumber`}
              required
              error={errors[`line_${orderIndex}_${lineItemIndex}_customerOrderNumber`]}
              placeholder="e.g., CO-12345"
              helperText="Customer order number (required)"
            />
            <TextInput
              label="Description"
              value={lineItem.description}
              onChange={(value) => onChange(orderIndex, lineItemIndex, 'description', value)}
              name={`orders[${orderIndex}].details[${lineItemIndex}].description`}
              required
              error={errors[`line_${orderIndex}_${lineItemIndex}_description`]}
              placeholder="e.g., Item description"
              helperText="Product item description (required)"
            />
            <TextInput
              label="Assigned ID"
              value={lineItem.orderAssignedId}
              onChange={(value) => onChange(orderIndex, lineItemIndex, 'orderAssignedId', value)}
              name={`orders[${orderIndex}].details[${lineItemIndex}].orderAssignedId`}
              required
              error={errors[`line_${orderIndex}_${lineItemIndex}_orderAssignedId`]}
              placeholder="e.g., 1"
              helperText="Line item sequence ID (required)"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Part Information</label>
              <button
                type="button"
                onClick={handleAddPart}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100"
              >
                <Plus size={14} />
                Add ID
              </button>
            </div>
            <div className="space-y-2">
              {lineItem.parts.map((part, partIndex) => (
                <div key={partIndex} className="flex gap-2">
                  <select
                    name={`orders[${orderIndex}].details[${lineItemIndex}].parts[${partIndex}].qualifier`}
                    value={part.qualifier}
                    onChange={(e) => handlePartChange(partIndex, 'qualifier', e.target.value)}
                    className="w-1/3 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  >
                    {PART_QUALIFIER_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    name={`orders[${orderIndex}].details[${lineItemIndex}].parts[${partIndex}].id`}
                    value={part.id}
                    onChange={(e) => handlePartChange(partIndex, 'id', e.target.value)}
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    placeholder="Part ID"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemovePart(partIndex)}
                    className="text-red-600 hover:text-red-700 p-1"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">References</label>
              <button
                type="button"
                onClick={handleAddReference}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100"
              >
                <Plus size={14} />
                Add Reference
              </button>
            </div>
            <div className="space-y-2">
              {lineItem.references.map((ref, refIndex) => (
                <div key={refIndex} className="flex gap-2">
                  <select
                    value={ref.qualifier}
                    onChange={(e) => handleReferenceChange(refIndex, 'qualifier', e.target.value)}
                    className="w-1/3 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  >
                    {REFERENCE_QUALIFIER_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={ref.id}
                    onChange={(e) => handleReferenceChange(refIndex, 'id', e.target.value)}
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    placeholder="Reference ID"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveReference(refIndex)}
                    className="text-red-600 hover:text-red-700 p-1"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Load Information</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <NumericInput
                label="Number of Loads"
                value={String(lineItem.loads.numberOfLoads)}
                onChange={(value) => handleLoadsChange('numberOfLoads', parseInt(value) || 1)}
                helperText="Total loads for this line"
              />
              <NumericInput
                label="Units in Loads"
                value={String(lineItem.loads.unitsShipped)}
                onChange={(value) => handleLoadsChange('unitsShipped', parseInt(value) || 0)}
                helperText="Units per load"
              />
              <SelectInput
                label="Packaging Code"
                value={lineItem.loads.packagingCode}
                onChange={(value) => handleLoadsChange('packagingCode', value)}
                options={PACKAGING_OPTIONS}
              />
            </div>

            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Serial Numbers</label>
              <button
                type="button"
                onClick={handleAddSerial}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100"
              >
                <Plus size={14} />
                Add Serial
              </button>
            </div>
            <div className="space-y-2">
              {lineItem.loads.serials.map((serial, serialIndex) => (
                <div key={serialIndex} className="flex gap-2">
                  <input
                    type="text"
                    value={serial}
                    onChange={(e) => handleSerialChange(serialIndex, e.target.value)}
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    placeholder="Serial number"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveSerial(serialIndex)}
                    className="text-red-600 hover:text-red-700 p-1"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
